/* RESPONSIBLE TEAM: team-omni */
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import { request, ResponseError } from 'embercom/lib/inbox/requests';

export interface DiscordUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  type: 'user' | 'usergroup';
}

interface DiscordUsersCache {
  users: DiscordUser[];
  timestamp: number;
}

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export default class DiscordUsersService extends Service {
  @service declare session: Session;

  @tracked isLoading = false;
  @tracked isSearching = false;
  @tracked error: Error | null = null;

  private cache: Map<string, DiscordUsersCache> = new Map();

  /**
   * Fetches Discord server members for a given Discord channel database ID
   * @param channelId - The Discord channel database ID
   * @param forceRefresh - Force a fresh fetch, bypassing cache
   * @returns Array of Discord users and roles
   */
  async fetchDiscordMembers(channelId: string, forceRefresh = false): Promise<DiscordUser[]> {
    if (!channelId) {
      return [];
    }

    if (!forceRefresh) {
      let cached = this.cache.get(channelId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
        return cached.users;
      }
    }

    this.isLoading = true;
    this.error = null;

    try {
      let response = await request(
        `/ember/discord/channels/${channelId}/fetch_members?app_id=${this.session.workspace.id}`,
      );

      let data = (await response.json()) as {
        users: Array<{
          id: string;
          username: string;
          display_name: string;
          avatar_url: string | null;
        }>;
        groups: Array<{
          id: string;
          username: string;
          display_name: string;
          avatar_url: string | null;
        }>;
      };

      let users: DiscordUser[] = (data.users || []).map((user) => ({
        id: user.id,
        username: user.username,
        display_name: user.display_name,
        avatar_url: user.avatar_url,
        type: 'user' as const,
      }));

      let groups: DiscordUser[] = (data.groups || []).map((group) => ({
        id: group.id,
        username: group.username,
        display_name: group.display_name,
        avatar_url: group.avatar_url,
        type: 'usergroup' as const,
      }));

      let allMembers = [...users, ...groups];

      this.cache.set(channelId, {
        users: allMembers,
        timestamp: Date.now(),
      });

      return allMembers;
    } catch (e: unknown) {
      if (e instanceof ResponseError && [401, 403].includes(e.response.status)) {
        console.warn(
          '[DiscordUsersService] Permission denied - admin lacks required permissions for Discord members',
        );
        this.error = new Error('Permission denied');
        this.cache.set(channelId, {
          users: [],
          timestamp: Date.now(),
        });
        return [];
      }

      console.error('[DiscordUsersService] Error fetching Discord members:', e);
      this.error = e instanceof Error ? e : new Error('Unknown error');

      let cached = this.cache.get(channelId);
      return cached?.users || [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Searches Discord server members by query using server-side search.
   * Unlike fetchDiscordMembers, results are not cached since each query is unique.
   * @param channelId - The Discord channel database ID
   * @param query - Search query string
   * @returns Array of matching Discord users and roles
   */
  async searchDiscordMembers(
    channelId: string,
    query: string,
    signal?: AbortSignal,
  ): Promise<DiscordUser[]> {
    if (!channelId || !query) {
      return [];
    }

    this.isSearching = true;
    this.error = null;

    try {
      let response = await request(
        `/ember/discord/channels/${channelId}/fetch_members?app_id=${this.session.workspace.id}&query=${encodeURIComponent(query)}`,
        { signal },
      );

      let data = (await response.json()) as {
        users: Array<{
          id: string;
          username: string;
          display_name: string;
          avatar_url: string | null;
        }>;
        groups: Array<{
          id: string;
          username: string;
          display_name: string;
          avatar_url: string | null;
        }>;
      };

      let users: DiscordUser[] = (data.users || []).map((user) => ({
        id: user.id,
        username: user.username,
        display_name: user.display_name,
        avatar_url: user.avatar_url,
        type: 'user' as const,
      }));

      let groups: DiscordUser[] = (data.groups || []).map((group) => ({
        id: group.id,
        username: group.username,
        display_name: group.display_name,
        avatar_url: group.avatar_url,
        type: 'usergroup' as const,
      }));

      return [...users, ...groups];
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw e;
      }

      if (e instanceof ResponseError && [401, 403].includes(e.response.status)) {
        console.warn(
          '[DiscordUsersService] Permission denied - admin lacks required permissions for Discord members',
        );
        this.error = new Error('Permission denied');
        return [];
      }

      console.error('[DiscordUsersService] Error searching Discord members:', e);
      this.error = e instanceof Error ? e : new Error('Unknown error');
      return [];
    } finally {
      this.isSearching = false;
    }
  }

  /**
   * Filters Discord users by search query
   * @param users - Array of Discord users
   * @param query - Search query string
   * @returns Filtered array of Discord users
   */
  filterUsers(users: DiscordUser[], query: string): DiscordUser[] {
    if (!query) {
      return users;
    }

    let lowerQuery = query.toLowerCase();
    return users.filter((user) => {
      let displayName = user.display_name?.toLowerCase() || '';
      let username = user.username.toLowerCase();
      return displayName.includes(lowerQuery) || username.includes(lowerQuery);
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'discord-users-service': DiscordUsersService;
  }
}
